<template>
  <component :is="currentStatusComponent" @redirect-to-merchant="redirectToMerchantSite" />
</template>

<script>
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex';
import IframeEventService from '@/services/IframeEventService';
import PaymentInvoiceStatusPage from './PaymentStatusPage.vue';
import BkashStatusPage from '@/pages/FlowInvoice/BkashPage/BkashStatusPage/Index.vue';
import EasypaisaStatusPage from '@/pages/FlowInvoice/EasypaisaPage/EasypaisaStatusPage/Index.vue';
import BlikInvoiceStatusPage from '@/pages/FlowInvoice/BlikInvoicePage/BlikStatusPage/Index.vue';

const methodStatusMap = {
  bkash_p2p: BkashStatusPage,
  easypaisa: EasypaisaStatusPage,
  jazzcash: EasypaisaStatusPage,
  blik: BlikInvoiceStatusPage,
};

const FINAL_STATUSES = new Set([
  'processed',
  'authorized',
  'partially_processed',
  'refunded',
  'revoked',
  'voided',
  'charged_back',
  'expired',
  'terminated',
  'invoke_failed',
  'process_failed',
]);

export default {
  name: 'PaymentStatusEntryPage',

  components: { PaymentInvoiceStatusPage, BkashStatusPage, EasypaisaStatusPage, BlikInvoiceStatusPage },

  beforeDestroy() {
    clearInterval(this.pollInterval);
    clearTimeout(this.redirectTimeout);
  },

  async created() {
    this.SET_CURRENT_STEP(3);
    try {
      const { id } = this.$route.params;
      this.paymentRequestId = id;
      await this.fetchStatus();
      this.pollInterval = setInterval(this.fetchStatus, 5000);
    } catch (e) {
      this.setGlobalError(e);
    }
  },

  data() {
    return {
      paymentRequestId: null,
      pollInterval: null,
      redirectTimeout: null,
      lastEmittedStatus: null,
      processedEventEmitted: false,
    };
  },

  computed: {
    ...mapState(['paymentInvoiceData', 'flowData']),
    ...mapGetters(['redirectDelay']),

    paymentInvoice() {
      return this.paymentInvoiceData?.data || this.paymentInvoiceData || {};
    },

    currentStatus() {
      return this.paymentInvoice.status || '';
    },

    isFinal() {
      return FINAL_STATUSES.has(this.currentStatus);
    },

    currentStatusComponent() {
      const serviceMethod = this.paymentInvoice?.service_method;
      return methodStatusMap[serviceMethod] || PaymentInvoiceStatusPage;
    },
  },

  methods: {
    ...mapActions(['getPaymentInvoiceById', 'setGlobalError']),
    ...mapMutations(['SET_CURRENT_STEP']),

    async fetchStatus() {
      if (this.isFinal) {
        clearInterval(this.pollInterval);
        this.handleFinalStatus();
        return;
      }

      try {
        await this.getPaymentInvoiceById(this.paymentRequestId);
      } catch (e) {
        if (IframeEventService.isInsideIframe()) {
          IframeEventService.emitError({
            message: e?.message || 'Status check failed',
            id: this.paymentRequestId,
          });
        }
      }

      const status = this.currentStatus;
      if (IframeEventService.isInsideIframe() && status && status !== this.lastEmittedStatus) {
        IframeEventService.emitStatusPage({
          status,
          resolution: this.paymentInvoice.resolution,
          id: this.paymentRequestId,
        });
        this.lastEmittedStatus = status;
      }

      if (this.isFinal) {
        clearInterval(this.pollInterval);
        this.handleFinalStatus();
      }
    },

    handleFinalStatus() {
      const { status, resolution, amount, currency } = this.paymentInvoice;
      if (IframeEventService.isInsideIframe() && !this.processedEventEmitted) {
        IframeEventService.emitProcessed({
          status,
          resolution,
          id: this.paymentRequestId,
          amount,
          currency,
        });
        this.processedEventEmitted = true;
      }
      if (this.paymentInvoice.has_return_url && this.paymentInvoice.service_method !== 'blik') {
        this.redirectTimeout = setTimeout(this.redirectToMerchantSite, this.redirectDelay);
      }
    },

    redirectToMerchantSite() {
      if (!this.paymentInvoice.has_return_url) return;
      const returnUrl = `/return?id=${this.paymentInvoice.id}&force=true`;
      if (IframeEventService.isInsideIframe()) {
        IframeEventService.emitRedirect(window.location.origin + returnUrl);
      }
      location.replace(returnUrl);
    },
  },
};
</script>